import { useEffect, useId, useRef, useState, type FocusEvent } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { ChevronDown, Menu, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useLocale, useAlternatePath, useHasAlternatePath, type Locale } from "@/lib/i18n";
import { Logo } from "@/components/site/Logo";
import { publicResourcesQuery } from "@/lib/query-registry";

type MegaLink = { to: string; en: string; es: string; descEn?: string; descEs?: string };
type MegaGroup = { headingEn: string; headingEs: string; links: MegaLink[] };
type MegaFeatured = {
  kind?: "static" | "latest-resource";
  surface?: "default" | "teal";
  eyebrowEn: string;
  eyebrowEs: string;
  titleEn: string;
  titleEs: string;
  descEn: string;
  descEs: string;
  /** Optional secondary evidence/proof line rendered under the description. */
  metaEn?: string;
  metaEs?: string;
  /** Optional local image asset shown in a neutral frame (object-contain). */
  imageSrc?: string;
  imageAlt?: string;
  ctaEn: string;
  ctaEs: string;
  to: string;
};

type NavItem = {
  to: string;
  en: string;
  es: string;
  mega?: { groups: MegaGroup[]; featured?: MegaFeatured };
};

const SERVICES_MEGA_EN: NonNullable<NavItem["mega"]> = {
  groups: [
    {
      headingEn: "Diagnose & fixed-scope",
      headingEs: "Diagnóstico y proyectos fijos",
      links: [
        { to: "/geo-readiness", en: "GEO Readiness Audit", es: "Auditoría GEO", descEn: "Paid diagnosis for AI visibility, citations and a prioritized 90-day roadmap." },
        { to: "/shopify-performance", en: "Performance Fix", es: "Fix de Performance", descEn: "Core Web Vitals, mobile reliability and app contention." },
        { to: "/shopify-cro", en: "CRO & Conversion", es: "CRO y Conversión", descEn: "PDP, cart, checkout and measurable conversion friction." },
        { to: "/shopify-technical-seo", en: "Technical SEO", es: "SEO Técnico", descEn: "Crawling, indexation, schema and information architecture." },
        { to: "/shopify-migration", en: "Shopify Migration", es: "Migración a Shopify", descEn: "WooCommerce, Magento, BigCommerce, custom platforms and legacy Shopify." },
        { to: "/shopify-experts", en: "Custom Build or Replatform", es: "Build Custom o Replatform", descEn: "Themes, storefronts and architecture with controlled releases." },
      ],
    },
    {
      headingEn: "Advanced & continuity",
      headingEs: "Capacidad avanzada y continuidad",
      links: [
        { to: "/hire-shopify-developer", en: "Hire a Shopify Developer", es: "Contratar un Desarrollador Shopify", descEn: "Senior engineering capacity for themes, Functions, checkout, apps and integrations." },
        { to: "/shopify-plus-agency", en: "Shopify Plus Engineering", es: "Ingeniería Shopify Plus", descEn: "B2B, Functions, checkout extensibility, complex Markets and governance." },
        { to: "/shopify-integration-services", en: "Integrations", es: "Integraciones", descEn: "ERP, OMS, PIM, WMS and 3PL pipelines that hold up in production." },
        { to: "/shopify-app-development", en: "App Development", es: "Desarrollo de apps", descEn: "Public, custom and embedded apps on Shopify's supported stack." },
        { to: "/analytics-ai", en: "Analytics & Applied AI", es: "Analítica e IA Aplicada", descEn: "Measurement contracts, reconciliation and validated decision systems." },
        { to: "/retainer", en: "Continuous Engineering Retainer", es: "Retainer de Ingeniería Continua", descEn: "Ongoing senior ownership after a defined project proves the working relationship." },
        { to: "/services", en: "All services overview", es: "Ver todos los servicios", descEn: "Compare diagnostics, fixed projects, Plus engineering and retainers." },
      ],
    },
  ],
  featured: {
    kind: "static",
    surface: "teal",
    eyebrowEn: "Start here",
    eyebrowEs: "Empieza aquí",
    titleEn: "Free Shopify audit",
    titleEs: "Auditoría Shopify gratuita",
    descEn: "A free signal scan of performance, CRO, SEO and AI readability. The result routes you to the right paid diagnostic or fixed-scope project.",
    descEs: "Un scan gratuito de performance, CRO, SEO y legibilidad por IA. El resultado te dirige al diagnóstico pagado o proyecto fijo correcto.",
    ctaEn: "Run the free audit",
    ctaEs: "Ejecutar auditoría gratis",
    to: "/audit",
  },
};

const SERVICES_MEGA_ES: NonNullable<NavItem["mega"]> = {
  groups: [
    {
      headingEn: "Diagnose & fixed-scope",
      headingEs: "Diagnóstico y proyectos fijos",
      links: [
        { to: "/geo-readiness", en: "GEO Readiness Audit", es: "Auditoría GEO", descEs: "Diagnóstico pagado de visibilidad en IA, citas y roadmap priorizado de 90 días." },
        { to: "/es/rendimiento", en: "Performance Fix", es: "Fix de Performance", descEs: "Core Web Vitals, confiabilidad móvil y conflictos entre apps." },
        { to: "/es/cro-shopify", en: "CRO & Conversion", es: "CRO y Conversión", descEs: "PDP, carrito, checkout y fricción medible de conversión." },
        { to: "/es/seo-tecnico-shopify", en: "Technical SEO", es: "SEO Técnico", descEs: "Crawling, indexación, schema y arquitectura de información." },
        { to: "/es/migracion-shopify", en: "Shopify Migration", es: "Migración a Shopify", descEs: "WooCommerce, Magento, BigCommerce, plataformas custom y Shopify legacy." },
        { to: "/es/desarrollo-shopify", en: "Custom Build or Replatform", es: "Build Custom o Replatform", descEs: "Themes, storefronts y arquitectura con releases controlados." },
      ],
    },
    {
      headingEn: "Advanced & continuity",
      headingEs: "Capacidad avanzada y continuidad",
      links: [
        { to: "/es/contratar-desarrollador-shopify", en: "Hire a Shopify Developer", es: "Contratar un Desarrollador Shopify", descEs: "Capacidad senior de ingeniería para themes, Functions, checkout, apps e integraciones." },
        { to: "/es/agencia-shopify-plus", en: "Shopify Plus Engineering", es: "Ingeniería Shopify Plus", descEs: "B2B, Functions, checkout extensibility, Markets complejos y gobierno." },
        { to: "/es/integraciones-shopify", en: "Integrations", es: "Integraciones", descEs: "ERP, OMS, PIM, WMS y 3PL que aguantan en producción." },
        { to: "/es/desarrollo-apps-shopify", en: "App Development", es: "Desarrollo de apps", descEs: "Apps públicas, custom y embebidas sobre el stack soportado por Shopify." },
        { to: "/es/analitica-ai", en: "Analytics & Applied AI", es: "Analítica e IA Aplicada", descEs: "Contratos de medición, conciliación y sistemas de decisión validados." },
        { to: "/es/retainer", en: "Continuous Engineering Retainer", es: "Retainer de Ingeniería Continua", descEs: "Ownership senior continuo después de demostrar la relación con un proyecto definido." },
        { to: "/es/servicios", en: "All services overview", es: "Ver todos los servicios", descEs: "Compara diagnósticos, proyectos fijos, ingeniería Plus y retainers." },
      ],
    },
  ],
  featured: {
    kind: "static",
    surface: "teal",
    eyebrowEn: "Start here",
    eyebrowEs: "Empieza aquí",
    titleEn: "Free Shopify audit",
    titleEs: "Auditoría Shopify gratuita",
    descEn: "A free signal scan of performance, CRO, SEO and AI readability.",
    descEs: "Un scan gratuito de performance, CRO, SEO y legibilidad por IA. El resultado te dirige al diagnóstico pagado o proyecto fijo correcto.",
    ctaEn: "Run the free audit",
    ctaEs: "Ejecutar auditoría gratis",
    to: "/es/auditoria",
  },
};

const RESOURCES_MEGA_EN: NonNullable<NavItem["mega"]> = {
  groups: [
    {
      headingEn: "Core guides",
      headingEs: "Guías principales",
      links: [
        { to: "/resources/shopify-performance-optimization-guide", en: "Shopify Performance Guide", es: "Guía de Rendimiento Shopify", descEn: "Speed, Core Web Vitals, app contention and the render path." },
        { to: "/resources/shopify-plus-migration", en: "Shopify Plus Migration Guide", es: "Guía de Migración a Shopify Plus", descEn: "Costs, realistic timelines, process, SEO continuity, checkout, B2B, integrations and controlled launch." },
        { to: "/resources/shopify-conversion-rate-optimization-playbook", en: "Shopify CRO Playbook", es: "Playbook de CRO Shopify", descEn: "PDP, cart, checkout, mobile UX and measurement." },
      ],
    },
    {
      headingEn: "Browse by topic",
      headingEs: "Explorar por tema",
      links: [
        { to: "/resources/performance", en: "Performance hub", es: "Hub de Rendimiento", descEn: "Guides, field notes and implementation decisions." },
        { to: "/resources/migration", en: "Migration hub", es: "Hub de Migración", descEn: "Standard Shopify, Plus decisions and source-platform planning." },
        { to: "/resources/conversion", en: "Conversion hub", es: "Hub de Conversión", descEn: "CRO, technical SEO, PDP, checkout and buying experience." },
        { to: "/resources", en: "All resources", es: "Todos los recursos", descEn: "Articles, playbooks, buyer guides and field notes." },
      ],
    },
  ],
  featured: {
    kind: "latest-resource",
    eyebrowEn: "Latest resource",
    eyebrowEs: "Último recurso",
    titleEn: "Latest resource",
    titleEs: "Último recurso",
    descEn: "The most recently published guide, playbook or field note from the resource library.",
    descEs: "La guía, playbook o nota de campo publicada más recientemente en la biblioteca de recursos.",
    ctaEn: "Read the resource",
    ctaEs: "Leer el recurso",
    to: "/resources",
  },
};


const RESOURCES_MEGA_ES: NonNullable<NavItem["mega"]> = {
  groups: [
    {
      headingEn: "Core guides",
      headingEs: "Guías principales",
      links: [
        { to: "/es/recursos/guia-optimizacion-rendimiento-shopify", en: "Shopify Performance Guide", es: "Guía de Rendimiento Shopify", descEs: "Velocidad, Core Web Vitals, conflictos de apps y render path." },
        { to: "/es/recursos/shopify-plus-migration", en: "Shopify Plus Migration Guide", es: "Guía de Migración a Shopify Plus", descEs: "Costos, tiempos realistas, proceso, continuidad SEO, checkout, B2B, integraciones y lanzamiento controlado." },
        { to: "/es/recursos/playbook-cro-shopify", en: "Shopify CRO Playbook", es: "Playbook de CRO Shopify", descEs: "PDP, carrito, checkout, UX móvil y medición." },
      ],
    },
    {
      headingEn: "Browse by topic",
      headingEs: "Explorar por tema",
      links: [
        { to: "/es/recursos/rendimiento", en: "Performance hub", es: "Hub de Rendimiento", descEs: "Guías, notas de campo y decisiones de implementación." },
        { to: "/es/recursos/migracion", en: "Migration hub", es: "Hub de Migración", descEs: "Shopify estándar, decisiones Plus y planificación por plataforma de origen." },
        { to: "/es/recursos/conversion", en: "Conversion hub", es: "Hub de Conversión", descEs: "CRO, SEO técnico, PDP, checkout y experiencia de compra." },
        { to: "/es/recursos", en: "All resources", es: "Todos los recursos", descEs: "Artículos, playbooks, guías de compra y notas de campo." },
      ],
    },
  ],
  featured: {
    kind: "latest-resource",
    eyebrowEn: "Latest resource",
    eyebrowEs: "Último recurso",
    titleEn: "Latest resource",
    titleEs: "Último recurso",
    descEn: "The most recently published guide, playbook or field note from the resource library.",
    descEs: "La guía, playbook o nota de campo publicada más recientemente en la biblioteca de recursos.",
    ctaEn: "Read the resource",
    ctaEs: "Leer el recurso",
    to: "/es/recursos",
  },
};

const NAV_EN: NavItem[] = [
  { to: "/services", en: "Services", es: "Servicios", mega: SERVICES_MEGA_EN },
  { to: "/work", en: "Work", es: "Proyectos" },
  { to: "/resources", en: "Resources", es: "Recursos", mega: RESOURCES_MEGA_EN },
  { to: "/blog", en: "Insights", es: "Insights" },
  { to: "/about", en: "About", es: "Nosotros" },

  { to: "/pricing", en: "Pricing", es: "Precios" },
  { to: "/contact", en: "Contact", es: "Contacto" },
];

const NAV_ES: NavItem[] = [
  { to: "/es/servicios", en: "Services", es: "Servicios", mega: SERVICES_MEGA_ES },
  { to: "/es/proyectos", en: "Work", es: "Proyectos" },
  { to: "/es/recursos", en: "Resources", es: "Recursos", mega: RESOURCES_MEGA_ES },
  { to: "/es/blog", en: "Blog", es: "Blog" },
  { to: "/es/nosotros", en: "About", es: "Nosotros" },
  { to: "/es/precios", en: "Pricing", es: "Precios" },
  { to: "/es/contacto", en: "Contact", es: "Contacto" },
];



export function SiteHeader() {
  const locale = useLocale();
  const items = locale === "es" ? NAV_ES : NAV_EN;
  const altLocale: Locale = locale === "es" ? "en" : "es";
  const altPath = useAlternatePath(altLocale);
  const showLangToggle = useHasAlternatePath();
  const quoteHref = locale === "es" ? "/es/cotizacion" : "/get-a-quote";
  const homeHref = locale === "es" ? "/es" : "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMobileMega, setOpenMobileMega] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setOpenMobileMega(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);

  useEffect(() => {
    let frame = 0;
    const update = () => { frame = 0; setScrolled(window.scrollY > 8); };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <header className={cn("fixed inset-x-0 top-0 z-50 transition-all duration-300", open ? "border-b border-border bg-background" : scrolled ? "border-b border-border/60 bg-background/80 backdrop-blur-xl" : "bg-transparent")}>
      <div className="relative z-50 mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4 md:py-5">
        <Link to={homeHref} aria-label="Shugert, home" className="flex items-center gap-2 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <Logo className="h-14 w-auto md:h-16" />
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
          {items.map((item) => item.mega ? <MegaMenu key={item.to} item={item} locale={locale} /> : (
            <Link key={item.to} to={item.to} className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" activeProps={{ className: "text-foreground" }}>
              {locale === "es" ? item.es : item.en}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {showLangToggle ? (
            <Link to={altPath} className="rounded-full border border-border px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={altLocale === "es" ? "Cambiar a Español" : "Switch to English"}>
              {altLocale === "es" ? "ES" : "EN"}
            </Link>
          ) : null}
          <Link to={quoteHref} className="btn-lift group relative inline-flex items-center gap-2 overflow-hidden rounded-full border border-[rgb(10_10_10/20%)] bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {locale === "es" ? "Cotizar" : "Get a quote"}<span aria-hidden>→</span>
          </Link>
        </div>

        <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? "Close menu" : "Open menu"} className="inline-flex touch-manipulation items-center justify-center rounded-full border border-border p-2 text-foreground transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open ? (
        <div id="mobile-nav" className="fixed inset-x-0 bottom-0 top-0 z-40 min-h-dvh overflow-y-auto overscroll-contain bg-background pb-[env(safe-area-inset-bottom)] lg:hidden">
          <div aria-hidden className="h-[88px]" />
          <nav aria-label="Mobile" className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-6">
            {items.map((item) => (
              <div key={item.to} className="flex flex-col">
                {item.mega ? (
                  <button type="button" aria-expanded={openMobileMega === item.to} onClick={() => setOpenMobileMega((current) => current === item.to ? null : item.to)} className="flex items-center justify-between rounded-lg px-3 py-3 text-left text-base font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <span>{locale === "es" ? item.es : item.en}</span>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", openMobileMega === item.to && "rotate-180")} aria-hidden />
                  </button>
                ) : (
                  <Link to={item.to} className="rounded-lg px-3 py-3 text-base font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground" activeProps={{ className: "text-foreground bg-card" }}>
                    {locale === "es" ? item.es : item.en}
                  </Link>
                )}
                {item.mega && openMobileMega === item.to ? (
                  <div className="pb-3">
                    <Link to={item.to} className="ml-3 mt-1 block rounded-lg px-3 py-2 text-sm font-semibold text-foreground hover:bg-card">{locale === "es" ? "Ver resumen" : `View ${item.en.toLowerCase()}`}</Link>
                    {item.mega.groups.map((group) => (
                      <div key={group.headingEn} className="ml-3 mt-2 flex flex-col border-l border-border pl-3">
                        <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{locale === "es" ? group.headingEs : group.headingEn}</p>
                        {group.links.map((child) => <Link key={child.to} to={child.to} className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-card hover:text-foreground">{locale === "es" ? child.es : child.en}</Link>)}
                      </div>
                    ))}
                    {item.mega.featured ? <FeaturedCard featured={item.mega.featured} locale={locale} mobile /> : null}
                  </div>
                ) : null}
              </div>
            ))}
            <div className="mt-4 flex items-center gap-3">
              {showLangToggle ? <Link to={altPath} className="rounded-full border border-border px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">{altLocale === "es" ? "Español" : "English"}</Link> : null}
              <Link to={quoteHref} className="btn-lift inline-flex flex-1 items-center justify-center rounded-full border border-[rgb(10_10_10/20%)] bg-primary px-5 py-3 text-sm font-medium text-primary-foreground">{locale === "es" ? "Cotizar proyecto" : "Get a quote"}</Link>
            </div>
          </nav>
        </div>
      ) : null}
    </header>
  );
}

function MegaMenu({ item, locale }: { item: NavItem; locale: Locale }) {
  const [open, setOpen] = useState(false);
  const label = locale === "es" ? item.es : item.en;
  const mega = item.mega!;
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();
  const coarse = useCoarsePointer();
  const location = useLocation();
  const clearClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { clearClose(); closeTimer.current = setTimeout(() => setOpen(false), 140); };
  const closeAndRestore = () => {
    const focusInside = typeof document !== "undefined" && wrapperRef.current?.contains(document.activeElement);
    setOpen(false);
    clearClose();
    if (focusInside) requestAnimationFrame(() => triggerRef.current?.focus());
  };
  const focusFirstLink = () => {
    clearClose();
    setOpen(true);
    requestAnimationFrame(() => panelRef.current?.querySelector<HTMLElement>("a")?.focus());
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") closeAndRestore(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  useEffect(() => () => clearClose(), []);
  useEffect(() => { setOpen(false); clearClose(); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) closeAndRestore();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const hoverHandlers = coarse ? {} : { onMouseEnter: () => { clearClose(); setOpen(true); }, onMouseLeave: scheduleClose };
  const panelHoverHandlers = coarse ? {} : { onMouseEnter: clearClose, onMouseLeave: scheduleClose };

  return (
    <div ref={wrapperRef} className="static" {...hoverHandlers} onFocus={() => { clearClose(); setOpen(true); }} onBlur={(event: FocusEvent<HTMLDivElement>) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) { clearClose(); setOpen(false); } }}>
      <span className="relative -mb-2 inline-flex items-center rounded-full pb-2">
        <Link to={item.to} className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" activeProps={{ className: "text-foreground" }} onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); focusFirstLink(); } }}>
          {label}
        </Link>
        <button ref={triggerRef} type="button" aria-haspopup="true" aria-expanded={open} aria-controls={panelId} aria-label={open ? `Close ${label} menu` : `Open ${label} menu`} onClick={() => setOpen((value) => !value)} onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); focusFirstLink(); } }} className="-ml-1 inline-flex items-center justify-center rounded-full p-1.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronDown className={cn("h-3.5 w-3.5 opacity-70 transition-transform", open && "rotate-180")} aria-hidden />
        </button>
      </span>
      {open ? (
        <div ref={panelRef} id={panelId} className="absolute inset-x-0 top-full z-50 px-6 pt-2" {...panelHoverHandlers}>
          <div className="site-mega-panel mx-auto grid max-w-7xl gap-6 rounded-2xl border border-border bg-background p-6 shadow-2xl lg:grid-cols-[1fr_1fr_320px]">
            {mega.groups.map((group) => (
              <div key={group.headingEn} className="flex flex-col">
                <p className="mb-3 px-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{locale === "es" ? group.headingEs : group.headingEn}</p>
                <div className="flex flex-col">
                  {group.links.map((child) => {
                    const description = locale === "es" ? child.descEs ?? child.descEn : child.descEn ?? child.descEs;
                    return (
                      <Link key={child.to} to={child.to} className="site-mega-link group/link block rounded-xl px-3 py-2.5 transition-colors hover:bg-card focus-visible:outline-none focus-visible:bg-card focus-visible:ring-2 focus-visible:ring-ring">
                        <div className="flex items-center gap-1 text-sm font-semibold text-foreground">{locale === "es" ? child.es : child.en}<span aria-hidden className="opacity-0 transition-opacity group-hover/link:opacity-100">→</span></div>
                        {description ? <div className="mt-0.5 text-xs leading-snug text-muted-foreground">{description}</div> : null}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
            {mega.featured ? <FeaturedCard featured={mega.featured} locale={locale} /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FeaturedCard({ featured, locale, mobile = false }: { featured: MegaFeatured; locale: Locale; mobile?: boolean }) {
  const isEs = locale === "es";
  const latest = useLatestResource(featured.kind === "latest-resource");
  let to = featured.to;
  let title = isEs ? featured.titleEs : featured.titleEn;
  let description = isEs ? featured.descEs : featured.descEn;
  let meta = isEs ? featured.metaEs : featured.metaEn;

  if (featured.kind === "latest-resource" && latest) {
    const slug = isEs ? (latest.slug_es ?? latest.slug) : latest.slug;
    to = isEs ? `/es/recursos/${slug}` : `/resources/${slug}`;
    title = isEs ? (latest.title_es || latest.title_en || title) : (latest.title_en || title);
    description = isEs ? (latest.excerpt_es || latest.excerpt_en || description) : (latest.excerpt_en || description);
    meta = undefined;
  }


  return (
    <Link
      to={to}
      className={cn(
        "site-mega-featured group/feat relative flex flex-col justify-between overflow-hidden rounded-2xl border p-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        featured.surface === "teal"
          ? "border-[rgba(15,118,110,0.20)] bg-[rgba(15,118,110,0.055)] hover:bg-[rgba(15,118,110,0.085)] dark:border-[rgba(94,234,212,0.24)] dark:bg-[rgba(15,118,110,0.13)]"
          : "border-border bg-card hover:bg-card",
        mobile && "ml-3 mt-3",
      )}
    >
      <div className="relative z-10">
        {featured.imageSrc && featured.kind !== "latest-resource" ? (
          <div className="mb-4 flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-xl border border-border bg-[#f2f2f0] p-2">
            <img src={featured.imageSrc} alt={featured.imageAlt ?? ""} loading="lazy" decoding="async" className="h-full w-full object-contain" />
          </div>
        ) : null}
        <span className={cn("text-[10px] font-semibold uppercase tracking-[0.24em]", featured.surface === "teal" ? "text-[#1f1f1f] dark:text-[#ccff00]" : "text-muted-foreground")}>{isEs ? featured.eyebrowEs : featured.eyebrowEn}</span>
        <h3 className="mt-2 font-display text-lg font-semibold leading-tight tracking-tight text-foreground">{title}</h3>
        <p className="mt-2 line-clamp-3 text-xs leading-snug text-muted-foreground">{description}</p>
        {meta ? <p className="mt-2 line-clamp-2 text-[11px] leading-snug text-muted-foreground/80">{meta}</p> : null}
      </div>

      <span className={cn("relative z-10 mt-4 inline-flex items-center gap-1 text-xs font-semibold", featured.surface === "teal" ? "text-[#1f1f1f] dark:text-[#ccff00]" : "text-foreground")}>{isEs ? featured.ctaEs : featured.ctaEn}<span aria-hidden>→</span></span>
    </Link>
  );
}

function useLatestResource(enabled: boolean) {
  const { data } = useQuery({ ...publicResourcesQuery(), enabled });
  const resources = Array.isArray(data) ? data : data?.resources;
  if (!enabled || !Array.isArray(resources) || resources.length === 0) return null;
  const sorted = [...resources].sort((a: any, b: any) => {
    const first = new Date(a.published_at ?? a.updated_at ?? 0).getTime();
    const second = new Date(b.published_at ?? b.updated_at ?? 0).getTime();
    return second - first;
  });
  return sorted[0] as any;
}

function useCoarsePointer() {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(hover: none), (pointer: coarse)");
    const update = () => setCoarse(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  return coarse;
}
